import React, { useEffect, useRef } from 'react';

// Focusable-element selector for the dialog focus trap.
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Inline init helper (mirrors the one in pages/time-logs.jsx; kept inline here
// to avoid cross-file coupling for a single 1-liner).
function init(name) {
  return (name || '').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
}

// AI scan & suggest modal.
// Pure presentation: scan logic + suggestion building live in the parent page.
//
// Props:
//   scanning        - bool, shows the spinner + "Scanning…" indicator
//   suggestions     - array of { pid, preset, why, need, confidence }
//                     confidence is an advisory band string:
//                     'High' | 'Medium' | 'Low' (not a probability).
//   patients        - array of adapted patients (with .id, .name, .avSlot)
//   model           - 'bedrock-haiku' | 'bedrock-sonnet' — current LLM choice
//   onModelChange(m)- swap the model (caller persists state)
//   onRerun()       - re-run the scan with the active model
//   onApply(s)      - apply a single suggestion (logs the preset for that pid)
//   onApplyAll()    - apply every suggestion at once
//   onDismiss(pid)  - drop a single suggestion from the list (no logging)
//   onClose()       - close the modal
export function ScanSuggest({ scanning, suggestions, patients, model, onModelChange, onRerun, onApply, onApplyAll, onDismiss, onClose }) {
  const totalMins = suggestions.reduce((s, x) => s + x.preset.mins, 0);
  const activeModel = model || 'bedrock-haiku';

  // Accessibility: trap focus inside the dialog while open, move focus in on
  // mount, restore it to the triggering element on close, and close on Escape.
  // Runs once on mount; onClose is read through a ref so a new prop identity
  // each render doesn't re-run the effect (which would yank focus back).
  const modalRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const node = modalRef.current;
    const prevActive = document.activeElement;
    if (node) {
      const focusables = node.querySelectorAll(FOCUSABLE);
      (focusables[0] || node).focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') { onCloseRef.current && onCloseRef.current(); return; }
      if (e.key !== 'Tab' || !node) return;
      const f = Array.from(node.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (prevActive && typeof prevActive.focus === 'function') prevActive.focus();
    };
  }, []);
  function pickModel(next) {
    if (!onModelChange || next === activeModel) return;
    onModelChange(next);
    // Re-run with the newly selected model so suggestions reflect it. Pass
    // the model explicitly so we don't race the React state update.
    if (onRerun) onRerun(next);
  }
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        ref={modalRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label="AI scan and suggest"
        tabIndex={-1}
        style={{maxWidth: 760}}
        onClick={e=>e.stopPropagation()}>
        <div className="modal-head">
          <div style={{display:'flex', gap:14, alignItems:'center'}}>
            <div style={{
              width:40, height:40, borderRadius:10,
              background:'linear-gradient(135deg, var(--accent), var(--accent-2))',
              color:'#fff', display:'flex', alignItems:'center', justifyContent:'center',
              boxShadow:'0 4px 14px -4px rgba(15,118,110,0.5)'
            }}>
              {scanning
                ? <span style={{display:'inline-block', width:18, height:18, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'spin 800ms linear infinite'}}/>
                : <b style={{fontSize:16}}>A</b>}
            </div>
            <div>
              <h3>AI scan &amp; suggest</h3>
              <div className="sub">
                {scanning ? 'Reading encounters, labs, and call logs…'
                          : suggestions.length ? `${suggestions.length} suggestions · +${totalMins}m to log`
                          : 'No suggestions generated for the current view.'}
              </div>
            </div>
          </div>
          <div style={{display:'flex', gap:10, alignItems:'center'}}>
            <div className="scan-model-toggle" role="group" aria-label="AI model">
              <button
                className={`filter-pill ${activeModel === 'bedrock-haiku' ? 'active' : ''}`}
                disabled={scanning}
                onClick={() => pickModel('bedrock-haiku')}
                title="Faster, cheaper. Good recall on common phrasing.">
                Haiku
              </button>
              <button
                className={`filter-pill ${activeModel === 'bedrock-sonnet' ? 'active' : ''}`}
                disabled={scanning}
                onClick={() => pickModel('bedrock-sonnet')}
                title="Slower, more thorough. Better recall on paraphrasing and clinical shorthand.">
                Sonnet
              </button>
            </div>
            <button className="btn ghost icon" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>

        <div className="modal-body" style={{maxHeight: '54vh', overflowY: 'auto'}}>
          {suggestions.length === 0 && !scanning && (
            <div style={{padding:'30px 10px', textAlign:'center', color:'var(--ink-3)', fontSize:13}}>
              No suggestions available — the heuristic found nothing to recommend for the patients in view.
            </div>
          )}
          {suggestions.map((s, i) => {
            const p = patients.find(x => x.id === s.pid);
            if (!p) return null;
            return (
              <div key={s.sid || `${s.pid}-${i}`} className="scan-row">
                <div className={`avatar av-${p.avSlot}`}>{init(p.name)}</div>
                <div className="scan-main">
                  <div className="scan-name">
                    {p.name}
                    <span className="scan-need tabnum">{s.need > 0 ? `${s.need}m short` : 'At target'}</span>
                  </div>
                  <div className="scan-why">{s.why}</div>
                </div>
                <div
                  className={`scan-confidence conf-${String(s.confidence || '').toLowerCase()}`}
                  title={
                    s.confidence === 'High'   ? 'Rule engine and LLM both identified this chip.'
                  : s.confidence === 'Medium' ? 'Either LLM only or rule engine only — partial signal.'
                  : 'No supporting evidence found beyond the rule heuristic.'
                  }
                >
                  <span className="conf-label">{s.confidence}</span>
                </div>
                <button className="preset-chip" onClick={()=>onApply(s)}>
                  <b>+{s.preset.mins}m</b> {s.preset.label}
                </button>
                <button className="btn ghost sm" onClick={()=>onDismiss(s.sid)}>Skip</button>
              </div>
            );
          })}
          {scanning && (
            <div style={{padding:'12px 4px', color:'var(--ink-3)', fontSize:12.5, display:'flex', gap:8, alignItems:'center'}}>
              <span style={{display:'inline-block', width:6, height:6, borderRadius:'50%', background:'var(--accent)', animation:'pulse 1.8s ease-in-out infinite'}}/>
              Scanning&hellip;
            </div>
          )}
        </div>

        <div className="modal-foot">
          <span style={{fontSize:12, color:'var(--ink-3)'}}>Suggestions are draft — every entry stays auditable.</span>
          <div style={{display:'flex', gap:8}}>
            <button className="btn" onClick={onClose}>Close</button>
            <button className="btn accent" disabled={scanning || suggestions.length === 0} onClick={onApplyAll}>
              Apply all <span style={{background:'rgba(255,255,255,0.16)', padding:'1px 6px', borderRadius:5, fontWeight:700, marginLeft:4}}>{suggestions.length}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
